import { TResponse } from '../../types/async';

import { IBuilderParams, TUrlBuilder } from './buildMcsUrl';
import { buildQueryFromStringAndParams } from './buildQueryFromStringAndParams';
import { getRequestHeaders } from './getRequestHeaders';
import { STATUS_UNAUTHORIZED } from './httpStatuses';

const METHOD_GET = 'GET';
const METHOD_POST = 'POST';

export interface IFetcherParams {
  url: string;
  method?: string;
  headers?: Headers;
  data?: any;
  queryParams?: Record<string, any>;
  queryString?: string;
  isExternal?: boolean;
  isLoggerOn?: boolean;
}

type Handler = (response: Response) => void;

// Silent-result контракт (I3 MR-2): различимые исходы вызова БЕЗ побочных эффектов
// (notifier/handleUnAuth) — для проб супервизора. Отличает invalid-response (2xx, но
// тело не распарсилось) от transport (fetch reject) — раньше оба сливались в один catch.
export type TSyncCallResult<T> =
  | { kind: 'ok'; status: number; data: T }
  | { kind: 'http'; status: number } // любой не-2xx
  | { kind: 'transport' } // fetch reject (сеть/CORS)
  | { kind: 'invalid-response'; status: number } // 2xx, но тело невалидно
  | { kind: 'cancelled' }; // AbortError (задел на будущее)

export class Fetcher {
  private apiUri: string;
  private handleUnAuth?: Handler;
  private buildMcsURL?: TUrlBuilder;

  constructor(apiUri = '', handleUnAuth?: Handler, builder?: TUrlBuilder) {
    this.apiUri = apiUri;
    this.handleUnAuth = handleUnAuth;
    this.buildMcsURL = builder;
  }

  // Клон без handleUnAuth: поздний 401 отменённой/фоновой пробы не должен глобально
  // ронять auth-сессию (супервизор обрабатывает 401 сам, session-aware). §2.4.
  public asProbe(): Fetcher {
    return new Fetcher(this.apiUri, undefined, this.buildMcsURL);
  }

  public buildURL(params: IBuilderParams) {
    if (typeof this.buildMcsURL === 'function') {
      return this.buildMcsURL(params);
    }

    throw new Error('UrlBuilder not provided!');
  }

  // Построение baseUrl + RequestInit — общая точка для fetch() и fetchResult().
  private buildRequest(params: IFetcherParams): { baseUrl: string; fetchOptions: RequestInit } {
    const {
      url,
      method,
      headers: additionalHeaders = {},
      queryParams,
      queryString,
      data,
      isExternal,
    } = params;

    const headers: { [key: string]: string } = {
      ...getRequestHeaders(),
      ...additionalHeaders,
    };

    let baseUrl: string;

    if (isExternal) {
      baseUrl = url;
    } else {
      if (!this.apiUri) {
        throw new Error('No apiUri provided in Fetcher!');
      }

      const fullQuery = buildQueryFromStringAndParams({ queryString, queryParams });
      baseUrl = `${this.apiUri}/${url}${fullQuery}`;
    }

    const fetchOptions: RequestInit = {
      headers,
      body: data ? JSON.stringify(data) : undefined,
      method: method || (data ? method || METHOD_POST : METHOD_GET),
      credentials: 'include',
    };

    return { baseUrl, fetchOptions };
  }

  // Silent-вариант: возвращает различимый TSyncCallResult без notifier'а; handleUnAuth
  // дёргается только если он есть у ЭТОГО инстанса (у asProbe() — нет). Отличает
  // invalid-response (2xx с невалидным телом) от transport (fetch reject). §2.4.
  public async fetchResult<T>(params: IFetcherParams): Promise<TSyncCallResult<T>> {
    const { baseUrl, fetchOptions } = this.buildRequest(params);

    let response: Response;
    try {
      response = await fetch(baseUrl, fetchOptions);
    } catch (error) {
      if (error && (error as { name?: string }).name === 'AbortError') {
        return { kind: 'cancelled' };
      }
      return { kind: 'transport' };
    }

    if (response.status === STATUS_UNAUTHORIZED && typeof this.handleUnAuth === 'function') {
      this.handleUnAuth(response);
    }

    if (!response.ok) {
      return { kind: 'http', status: response.status };
    }

    try {
      const parsed = await response.json();
      const data = (typeof parsed?.data !== 'undefined' ? parsed.data : parsed) as T;
      // ВНИМАНИЕ: kind:'ok' означает лишь «2xx + тело успешно распарсилось». Схема НЕ
      // валидируется (cast as T) — обязательность полей проверяет вызывающий (супервизор),
      // напр. probeInfo может вернуть ok с customer:undefined на пустом {}.
      return { kind: 'ok', status: response.status, data };
    } catch {
      // 2xx, но тело не распарсилось/пустое — это НЕ сетевой сбой.
      return { kind: 'invalid-response', status: response.status };
    }
  }

  public fetch<T>(params: IFetcherParams) {
    const { isLoggerOn } = params;
    const { baseUrl, fetchOptions } = this.buildRequest(params);

    return fetch(baseUrl, fetchOptions)
      .then(async (response: Response) => {
        if (response.status === STATUS_UNAUTHORIZED) {
          if (typeof this.handleUnAuth === 'function') {
            this.handleUnAuth(response);
          }
        }

        // Не-2xx: НЕ бросаем — иначе теряется HTTP-статус (истинный сетевой reject
        // отличается от прикладной ошибки только наличием статуса). Возвращаем
        // маркированную error-форму без парсинга тела (json() на не-JSON/пустом падает).
        if (!response.ok) {
          return {
            __httpError: true as const,
            status: response.status,
            error: response.statusText || `HTTP ${response.status}`,
          };
        }

        return {
          status: response.status,
          response: await response.json(),
        };
      })
      .then((result) => {
        if ('__httpError' in result) {
          return {
            status: result.status,
            data: null,
            error: result.error,
          } as TResponse<T>;
        }

        const { response: parsedResponse, status } = result;

        if (isLoggerOn) {
          console.log('parsedResponse', parsedResponse);
          console.log('parsedResponse.data', parsedResponse.data);
        }

        const preparedResponse = {
          status,
          data: typeof parsedResponse.data !== 'undefined' ? parsedResponse.data : parsedResponse,
          error: null,
        } as TResponse<T>;

        if (isLoggerOn) {
          console.log('preparedResponse', preparedResponse);
        }

        return preparedResponse;
      })
      .catch((error) => {
        // Истинный сетевой сбой (fetch reject) — ответа нет, статуса нет.
        console.error(`
          => Request to ${baseUrl} falied

          Error: ${error}
          _______________

        `);

        return {
          data: null,
          error,
        } as TResponse<T>;
      });
  }
}
